import type { ReactNode } from 'react';
import type { ButtonProps } from '../Button/Button';
import type { PopoverPlacementOptions } from '../Popover/Popover';

import { useCallback, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

import { EllipsisIcon } from '../../images/shapes';

import { PopoverPanelPlacement } from '../Popover/classes';
import PopoverComponent from '../Popover/Popover';
import PopoverItem from '../Popover/PopoverItem/PopoverItem';

type ActionMenuClickFn<T> = (item: ActionMenuItem<T>) => void;

type ActionMenuItem<T> = T & {
  key: string;
  label?: ReactNode;
  onClick?: ActionMenuClickFn<T>;
  icon?: ReactNode;
  additionalClasses?: string;
};

interface ActionMenuProps<T> extends Omit<ButtonProps, 'onClick'> {
  /**
   * Action menu items
   */
  menuItems: ActionMenuItem<T>[];
  /**
   * Button
   */
  button?: ReactNode;
  /**
   * On items click handler
   */
  onClick?: (item: ActionMenuItem<T>) => void;
  /**
   * Additional CSS classes for an Action menu component
   */
  additionalActionMenuClasses?: string;
  /**
   * Additional CSS classes for popover panel component
   */
  additionalPopoverPanelClasses?: string;
  /**
   * Action menu placement on desktop
   */
  actionMenuPlacement?: PopoverPlacementOptions;
  /**
   * Action menu placement on phone
   */
  actionMenuPhonePlacement?: PopoverPlacementOptions;
  /**
   * Action menu test id
   */
  testId?: string;
}

const ActionMenu = <T extends Record<string, unknown>>({
  menuItems,
  button,
  onClick,
  actionMenuPlacement = PopoverPanelPlacement.RightBottom,
  actionMenuPhonePlacement,
  additionalActionMenuClasses,
  additionalPopoverPanelClasses = '',
  testId = '',
  ...props
}: ActionMenuProps<T>) => {
  const handleOnClick = useCallback<ActionMenuClickFn<T>>(
    (item) => {
      item.onClick?.(item);
      onClick?.(item);
    },
    [onClick],
  );

  const popoverContent = useMemo(
    () =>
      menuItems.map((item) => {
        const { key, ...props } = item;
        return (
          <PopoverItem
            key={key}
            {...props}
            onClick={() => handleOnClick(item)}
            {...getTestProps(testId, `${key}-item`, 'testId')}
          />
        );
      }),
    [handleOnClick, menuItems, testId],
  );
  const ActionMenuButton = useMemo(() => {
    if (!button)
      return (
        <EllipsisIcon className="w-4 2xl:w-5 h-4 2xl:h-5 text-slate-400 group-hover:text-blue" />
      );
    return button;
  }, [button]);

  return (
    <PopoverComponent
      testId={testId}
      popoverPanelPlacement={actionMenuPlacement}
      popoverPhonePanelPlacement={actionMenuPhonePlacement}
      additionalPopoverPanelClasses={twMerge(
        'z-30',
        additionalPopoverPanelClasses,
      )}
      content={popoverContent}
      popoverButton={ActionMenuButton}
      popoverButtonProps={{
        isOurButton: true,
        buttonSize: 'lg',
        ...props,
      }}
      additionalClasses={twMerge(additionalActionMenuClasses)}
    />
  );
};

export default ActionMenu;
